import { html } from 'htm/preact'

export function SystemHealth({ data }) {
  if (!data) return null

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100 border-green-200 dark:border-green-700'
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100 border-yellow-200 dark:border-yellow-700'
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100 border-red-200 dark:border-red-700'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return html`
          <svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
          </svg>
        `
      case 'warning':
        return html`
          <svg class="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
          </svg>
        `
      case 'critical':
        return html`
          <svg class="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
          </svg>
        `
      default:
        return null
    }
  }

  return html`
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
      <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          ${getStatusIcon(data.status)}
          System Health
        </h2>
      </div>
      
      <div class="p-6">
        <!-- Overall Status -->
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white">
              System Status
            </h3>
            <p class="text-gray-600 dark:text-gray-400">
              Overall system health assessment
            </p>
          </div>
          
          <div class="text-right">
            <span class="${getStatusColor(data.status)} px-4 py-2 rounded-full text-sm font-medium border uppercase">
              ${data.status}
            </span>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
              ${new Date(data.timestamp).toLocaleTimeString()}
            </p>
          </div>
        </div>

        <!-- Health Details Grid -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 dark:text-gray-400">Scheduler</p>
                <p class="text-lg font-semibold ${data.details.scheduler_running ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}">
                  ${data.details.scheduler_running ? 'Running' : 'Stopped'}
                </p>
              </div>
              <div class="w-3 h-3 rounded-full ${data.details.scheduler_running ? 'bg-green-500' : 'bg-red-500'}"></div>
            </div>
          </div>

          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 dark:text-gray-400">Stuck Sources</p>
                <p class="text-lg font-semibold ${data.details.stuck_sources_count === 0 ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}">
                  ${data.details.stuck_sources_count}
                </p>
              </div>
              <svg class="w-5 h-5 ${data.details.stuck_sources_count === 0 ? 'text-green-500' : 'text-yellow-500'}" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L9.414 11H13a1 1 0 100-2H9.414l1.293-1.293z" clip-rule="evenodd"/>
              </svg>
            </div>
          </div>

          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 dark:text-gray-400">Failing Sources</p>
                <p class="text-lg font-semibold ${data.details.failing_sources_count === 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}">
                  ${data.details.failing_sources_count}
                </p>
              </div>
              <svg class="w-5 h-5 ${data.details.failing_sources_count === 0 ? 'text-green-500' : 'text-red-500'}" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
              </svg>
            </div>
          </div>

          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 dark:text-gray-400">Recent Articles</p>
                <p class="text-lg font-semibold ${data.details.recent_articles_count > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}">
                  ${data.details.recent_articles_count}
                </p>
              </div>
              <svg class="w-5 h-5 ${data.details.recent_articles_count > 0 ? 'text-green-500' : 'text-gray-500'}" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 01-1.5.866L12 15.5l-2.5 1.366A1 1 0 018 16V4z" clip-rule="evenodd"/>
              </svg>
            </div>
          </div>
        </div>

        <!-- Issues -->
        ${data.issues && data.issues.length > 0 ? html`
          <div class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <h4 class="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
              Issues Detected (${data.issues.length})
            </h4>
            <ul class="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
              ${data.issues.map(issue => html`
                <li key=${issue} class="flex items-center gap-2">
                  <svg class="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                  </svg>
                  ${issue}
                </li>
              `)}
            </ul>
          </div>
        ` : html`
          <div class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div class="flex items-center gap-2">
              <svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
              <span class="text-sm font-medium text-green-800 dark:text-green-200">
                No issues detected - System is operating normally
              </span>
            </div>
          </div>
        `}
      </div>
    </div>
  `
}